import type { ReactNode } from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib/utils"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex flex-col gap-2",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col",
  {
    variants: {
      variant: {
        default: "gap-3 bg-transparent p-0",
        line: "mb-8 w-full justify-start gap-3 border-b-[0.5px] border-b-[var(--rule)] bg-transparent p-0",
        pill: "gap-1 rounded-lg bg-muted p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  children,
  badge,
  ...props
}: TabsPrimitive.Tab.Props & {
  /**
   * Optional trailing adornment (e.g. a `NotificationDot` or `Badge`) rendered
   * after the tab label. Use it to draw attention to a tab whose content needs
   * action but would otherwise be hidden behind the tab.
   */
  badge?: ReactNode
}) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-all duration-150 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=default]/tabs-list:h-auto group-data-[variant=default]/tabs-list:flex-none group-data-[variant=default]/tabs-list:rounded-none group-data-[variant=default]/tabs-list:border-b-[1.5px] group-data-[variant=default]/tabs-list:border-b-transparent group-data-[variant=default]/tabs-list:px-0 group-data-[variant=default]/tabs-list:py-0.5 group-data-[variant=default]/tabs-list:text-sm group-data-[variant=default]/tabs-list:uppercase group-data-[variant=default]/tabs-list:tracking-[0.12em] group-data-[variant=default]/tabs-list:text-[var(--ink-3)] group-data-[variant=default]/tabs-list:hover:text-[var(--ink)] group-data-[variant=default]/tabs-list:data-active:border-b-[var(--ink)] group-data-[variant=default]/tabs-list:data-active:text-[var(--ink)] group-data-[variant=default]/tabs-list:data-active:font-normal",
        "group-data-[variant=line]/tabs-list:mb-[-0.5px] group-data-[variant=line]/tabs-list:h-auto group-data-[variant=line]/tabs-list:flex-none group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:border-b-[1.5px] group-data-[variant=line]/tabs-list:border-b-transparent group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:px-0 group-data-[variant=line]/tabs-list:pb-3 group-data-[variant=line]/tabs-list:pt-0 group-data-[variant=line]/tabs-list:text-sm group-data-[variant=line]/tabs-list:font-normal group-data-[variant=line]/tabs-list:uppercase group-data-[variant=line]/tabs-list:tracking-[0.1em] group-data-[variant=line]/tabs-list:text-[var(--ink-3)] group-data-[variant=line]/tabs-list:hover:bg-transparent group-data-[variant=line]/tabs-list:hover:text-[var(--ink)] group-data-[variant=line]/tabs-list:data-active:border-b-[var(--green)] group-data-[variant=line]/tabs-list:data-active:text-[var(--ink)] group-data-[variant=line]/tabs-list:data-active:font-normal",
        "group-data-[variant=pill]/tabs-list:h-[calc(100%-1px)] group-data-[variant=pill]/tabs-list:flex-1 group-data-[variant=pill]/tabs-list:rounded-md group-data-[variant=pill]/tabs-list:border group-data-[variant=pill]/tabs-list:border-transparent group-data-[variant=pill]/tabs-list:px-1.5 group-data-[variant=pill]/tabs-list:py-0.5 group-data-[variant=pill]/tabs-list:text-base group-data-[variant=pill]/tabs-list:font-medium group-data-[variant=pill]/tabs-list:text-foreground/60 group-data-[variant=pill]/tabs-list:hover:text-foreground group-data-[variant=pill]/tabs-list:data-active:bg-background group-data-[variant=pill]/tabs-list:data-active:text-foreground dark:group-data-[variant=pill]/tabs-list:text-muted-foreground dark:group-data-[variant=pill]/tabs-list:hover:text-foreground dark:group-data-[variant=pill]/tabs-list:data-active:border-input dark:group-data-[variant=pill]/tabs-list:data-active:bg-input/30 dark:group-data-[variant=pill]/tabs-list:data-active:text-foreground group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start",
        className
      )}
      {...props}
    >
      {children}
      {badge}
    </TabsPrimitive.Tab>
  );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("text-base outline-none", className)}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
